// Pure list logic for the Preschool invoices page: statuses, filters, grouping by child, children not invoiced
// yet, and per-term parent and council funding totals.

import type { FundingPayment, PreschoolChild, PreschoolInvoice } from "../../shared/preschoolInvoices";

export type InvoiceStatus = "notIssued" | "unpaid" | "paid" | "nothingDue" | "cancelled";

export const STATUS_LABEL: Record<InvoiceStatus, string> = {
  notIssued: "Not issued", unpaid: "Unpaid", paid: "Paid", nothingDue: "Nothing to pay", cancelled: "Cancelled",
};

export const statusOf = (invoice: PreschoolInvoice): InvoiceStatus =>
  invoice.cancelled ? "cancelled"
    : invoice.notIssued ? "notIssued"
      : invoice.totalPence <= 0 && !invoice.paid ? "nothingDue"
        : invoice.paid ? "paid" : "unpaid";

/** Newest first: invoice numbers are issued in order. */
export const byNewest = (a: PreschoolInvoice, b: PreschoolInvoice) => b.invoiceNo - a.invoiceNo;

export const matchesSearch = (invoice: PreschoolInvoice, search: string) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [invoice.ref, invoice.childName, invoice.parentName, invoice.email, invoice.period]
    .some((text) => (text || "").toLowerCase().includes(needle));
};

export interface ChildGroup {
  childId: string;
  childName: string;
  parentName: string;
  invoices: PreschoolInvoice[];
  parentPence: number;
  outstandingPence: number;
}

/** One group per child, newest invoice first inside, groups ordered by their newest invoice. */
export const groupByChild = (invoices: PreschoolInvoice[]): ChildGroup[] => {
  const groups = new Map<string, ChildGroup>();
  for (const invoice of [...invoices].sort(byNewest)) {
    const group = groups.get(invoice.childId) ?? {
      childId: invoice.childId, childName: invoice.childName, parentName: invoice.parentName,
      invoices: [], parentPence: 0, outstandingPence: 0,
    };
    group.invoices.push(invoice);
    if (!invoice.cancelled && !invoice.notIssued) {
      group.parentPence += invoice.totalPence;
      if (statusOf(invoice) === "unpaid") group.outstandingPence += invoice.totalPence;
    }
    groups.set(invoice.childId, group);
  }
  return [...groups.values()];
};

export interface NotInvoiced {
  child: PreschoolChild;
  /** The child's newest earlier term invoice, used to prefill. */
  previous: PreschoolInvoice | null;
}

/** Active children with no (non-cancelled) childcare invoice in the term; registration invoices don't count. */
export const notInvoiced = (children: PreschoolChild[], invoices: PreschoolInvoice[], termId: string): NotInvoiced[] => {
  const covered = new Set(invoices.filter((i) => i.termId === termId && !i.cancelled && i.kind !== "registration").map((i) => i.childId));
  return children.filter((child) => !covered.has(child.id)).map((child) => ({
    child,
    previous: invoices.filter((i) => i.childId === child.id && i.kind === "term" && i.calc && !i.cancelled).sort(byNewest)[0] ?? null,
  }));
};

export interface Totals {
  parentPence: number;
  paidPence: number;
  outstandingPence: number;
  unpaidCount: number;
  fundingExpectedPence: number;
  fundingReceivedPence: number;
}

/** Parent totals count issued invoices only; expected funding counts every invoice that is not cancelled. */
export const totals = (invoices: PreschoolInvoice[], payments: FundingPayment[]): Totals => {
  const live = invoices.filter((i) => !i.cancelled && !i.notIssued);
  const unpaid = live.filter((i) => statusOf(i) === "unpaid");
  const parentPence = live.reduce((sum, i) => sum + i.totalPence, 0);
  const outstandingPence = unpaid.reduce((sum, i) => sum + i.totalPence, 0);
  return {
    parentPence,
    outstandingPence,
    paidPence: parentPence - outstandingPence,
    unpaidCount: unpaid.length,
    fundingExpectedPence: invoices.filter((i) => !i.cancelled).reduce((sum, i) => sum + (i.fundingPence || 0), 0),
    fundingReceivedPence: payments.reduce((sum, p) => sum + p.amountPence, 0),
  };
};
